import { useExternalStoreRuntime } from '@assistant-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import {
    ChatCompletionTimeoutMs,
    ChatErrorKind,
    ChatEventType,
    ChatRequestLifecycleEvent,
    ChatRole,
    ChatStartTimeoutMs,
    CHAT_ACTION_TYPE,
    CHAT_COMPLETED_TYPE,
    CHAT_FAILED_TYPE,
    CHAT_RECEIVE_MESSAGE_TYPE,
    CHAT_RESPONSE_COMPLETED_TYPE,
    CHAT_RETRIED_TYPE,
    CHAT_SEND_MESSAGE_TYPE,
    CHAT_STARTED_TYPE,
    EmptyAssistantDraft,
} from 'utils/chat/chatConstants';
import { getAiChatPayload, getLatestMessageContext, toAssistantMessages } from 'utils/chat/chatHelpers';
import {
    buildAssistantMessageFromCompletion,
    ChatStreamUpdateMode,
    getEventConversationId,
    getFinalPayloadFromRuntimeEvent,
    getOutOfSyncChatReconciliation,
    getRequestedInteractionFromAction,
    getStreamTextUpdateFromRuntimeEvent,
    isEventForActiveRequest,
    isUnsupportedRequestError,
    mergeCompletionPayload,
    normalizeFailureKind,
    StreamFinalizationDelayMs,
} from 'utils/chat/chatProtocolAdapters';
import { createVisualStreamController } from 'utils/chat/VisualStreamController';
import useChatError from './useChatError';

const buildRetryRequestSnapshot = (userMessage) => {
    return {
        createdOn: userMessage.created_on,
        messageContext: userMessage.message_context,
        messageText: userMessage.message_text,
        userMessageId: userMessage.id,
    };
};

const buildRetryUserMessage = ({ fallbackMessageContext, retryRequest }) => {
    return {
        id: retryRequest.userMessageId,
        active: true,
        created_on: retryRequest.createdOn,
        modified_on: Date.now(),
        role: ChatRole.USER,
        message_text: retryRequest.messageText,
        message_context: retryRequest.messageContext || fallbackMessageContext,
    };
};

const hasPersistedAssistantResponseForRequest = ({ chat, request }) => {
    if (!chat || !request) return false;

    return (chat.messages || []).some((message) => {
        return (
            message.active !== false &&
            message.role === ChatRole.ASSISTANT &&
            (message.created_on || 0) >= request.userMessageCreatedOn
        );
    });
};

const buildOutgoingChatRequest = ({ assignmentId, chatId, fileKey, requestId, userMessage }) => {
    return {
        type: CHAT_SEND_MESSAGE_TYPE,
        id: requestId,
        chat_id: chatId,
        assignment_id: assignmentId,
        created_on: userMessage.created_on,
        file_key: fileKey,
        message_id: userMessage.id,
        message_text: userMessage.message_text,
        ...(userMessage.interaction_response ? { interaction_response: userMessage.interaction_response } : {}),
    };
};

const buildDefaultOutgoingChatRequest = ({ assignmentId, chatId, fileKey, requestId, userMessage }) =>
    buildOutgoingChatRequest({ assignmentId, chatId, fileKey, requestId, userMessage });

const buildTransientAssistantMessage = ({ createdOn, id, text }) => {
    return {
        active: true,
        created_on: createdOn,
        id,
        is_streaming: true,
        message_context: null,
        message_text: text,
        modified_on: createdOn,
        role: ChatRole.ASSISTANT,
    };
};

/**
 * Runtime adapter between persisted chat data, websocket AI events, and assistant-ui.
 *
 * Use inside `AssistantChatRuntime`, not directly from page code. It turns persisted chat
 * messages into assistant-ui messages, sends user prompts over websocket, streams assistant
 * drafts, handles retries/errors, and reports persisted state changes back to the thread hook.
 *
 * @param {Object} props
 * @param {Object|null} props.chat Persisted chat with messages and processing state.
 * @param {string} [props.fileKey] Current submission file key sent with user prompts.
 * @param {boolean} props.isConnected Whether the websocket is currently connected.
 * @param {Function} props.onChatRequestStateChange Receives optimistic persistence events for the thread hook.
 * @param {Function} props.onMessage Websocket subscription function.
 * @param {Function} props.sendMessage Websocket send function.
 * @returns {{chatRuntimeContextValue: Object, runtime: Object}} Context value and assistant-ui runtime.
 */
const useAssistantChatController = ({
    buildOutgoingRequest = buildDefaultOutgoingChatRequest,
    chat,
    fileKey,
    isConnected,
    onChatRequestStateChange,
    onMessage,
    sendMessage,
    threadListAdapter,
}) => {
    const assistantMessages = useMemo(() => toAssistantMessages(chat?.messages || []), [chat?.messages]);
    const chatId = chat?.id;
    const threadRuntimeKey = chat?.id || chat?.local_id;
    const assignmentId = chat?.assignment_id;
    const chatIsProcessing = Boolean(chat?.is_processing);
    const latestMessageContext = useMemo(() => getLatestMessageContext(chat?.messages || []), [chat?.messages]);

    const { buildChatError, chatError, clearChatError, setChatError } = useChatError();
    const [manualAssistantDraft, setManualAssistantDraft] = useState(EmptyAssistantDraft);
    const [isRetrying, setIsRetrying] = useState(false);
    const [runtimeIsRunning, setRuntimeIsRunning] = useState(false);

    const activeRequestRef = useRef(null);
    const isRuntimeReconciliationBlockedRef = useRef(false);
    const transientAssistantMessageRef = useRef(null);
    const isConnectedRef = useRef(isConnected);
    const retryRequestRef = useRef(null);

    useEffect(() => {
        isConnectedRef.current = isConnected;
    }, [isConnected]);

    const setRuntimeReconciliationBlocked = useCallback(
        (isBlocked) => {
            if (isRuntimeReconciliationBlockedRef.current === isBlocked) return;

            isRuntimeReconciliationBlockedRef.current = isBlocked;
            onChatRequestStateChange?.({
                type: ChatRequestLifecycleEvent.RUNTIME_STATE_CHANGED,
                blocksExternalReconciliation: isBlocked,
            });
        },
        [onChatRequestStateChange]
    );

    const clearActiveRequest = useCallback(({ cancelPendingResult = false } = {}) => {
        const activeRequest = activeRequestRef.current;
        if (!activeRequest) return;

        if (cancelPendingResult) {
            activeRequest.cancelPendingResult?.();
        }

        if (activeRequest.startTimeoutId) {
            clearTimeout(activeRequest.startTimeoutId);
        }

        if (activeRequest.stallTimeoutId) {
            clearTimeout(activeRequest.stallTimeoutId);
        }

        if (activeRequest.unsubscribe) {
            activeRequest.unsubscribe();
        }

        activeRequestRef.current = null;
    }, []);

    const resetTransientRuntimeState = useCallback(() => {
        setIsRetrying(false);
        setManualAssistantDraft(EmptyAssistantDraft);
        setRuntimeIsRunning(false);
    }, []);

    const resetRuntimeForChatReconciliation = useCallback(() => {
        clearActiveRequest();
        clearChatError();
        resetTransientRuntimeState();
        setRuntimeReconciliationBlocked(false);
        transientAssistantMessageRef.current = null;
    }, [clearActiveRequest, clearChatError, resetTransientRuntimeState, setRuntimeReconciliationBlocked]);

    const applyRequestFailure = useCallback(
        (failureReason) => {
            const failureKind = normalizeFailureKind(failureReason);
            clearActiveRequest();
            resetTransientRuntimeState();
            onChatRequestStateChange?.({
                type: ChatRequestLifecycleEvent.REQUEST_FAILED,
                failureKind,
            });
            setChatError(buildChatError(failureKind));
        },
        [buildChatError, clearActiveRequest, onChatRequestStateChange, resetTransientRuntimeState, setChatError]
    );

    const executeRequest = useCallback(
        ({ onFailure, onFinal, onStreamUpdate, shouldCreateUserMessage, userMessage }) => {
            if (!sendMessage) {
                onFailure?.(ChatErrorKind.GENERATION);
                return Promise.resolve(null);
            }

            setRuntimeIsRunning(true);
            transientAssistantMessageRef.current = {
                createdOn: Date.now(),
                id: uuid(),
            };

            return new Promise((resolve) => {
                const requestId = uuid();
                let accumulatedStreamText = EmptyAssistantDraft;
                let didSettleRequest = false;
                let didCancelPendingResult = false;
                let finalizationTimeoutId = null;
                let pendingActionPayload = null;
                const visualStreamController = createVisualStreamController({
                    onReconciliationBlockedChange: setRuntimeReconciliationBlocked,
                    onStreamUpdate,
                });

                const clearFinalizationTimeout = () => {
                    if (!finalizationTimeoutId) return;
                    clearTimeout(finalizationTimeoutId);
                    finalizationTimeoutId = null;
                };

                const finalizeFailedRequest = (failureReason) => {
                    if (didSettleRequest) return;
                    didSettleRequest = true;
                    clearFinalizationTimeout();
                    visualStreamController.cancel();
                    clearActiveRequest();
                    setRuntimeIsRunning(false);
                    transientAssistantMessageRef.current = null;
                    onFailure?.(failureReason);
                    resolve(null);
                };

                const finalizeSuccessfulRequest = (payload = {}) => {
                    if (didSettleRequest) return;
                    didSettleRequest = true;
                    const finalPayload = mergeCompletionPayload({
                        pendingActionPayload,
                        payload,
                        streamedText: accumulatedStreamText,
                    });
                    if (finalPayload.message_id) {
                        transientAssistantMessageRef.current = {
                            ...(transientAssistantMessageRef.current || {}),
                            id: finalPayload.message_id,
                        };
                    }
                    const assistantMessage = buildAssistantMessageFromCompletion({
                        accumulatedText: accumulatedStreamText,
                        fallbackMessageContext: latestMessageContext,
                        payload: finalPayload,
                    });

                    const activeRequest = activeRequestRef.current;
                    if (activeRequest?.startTimeoutId) {
                        clearTimeout(activeRequest.startTimeoutId);
                        activeRequest.startTimeoutId = null;
                    }
                    if (activeRequest?.stallTimeoutId) {
                        clearTimeout(activeRequest.stallTimeoutId);
                        activeRequest.stallTimeoutId = null;
                    }
                    if (activeRequest?.unsubscribe) {
                        activeRequest.unsubscribe();
                        activeRequest.unsubscribe = null;
                    }

                    const completeRequest = () => {
                        if (didCancelPendingResult) {
                            resolve(null);
                            return;
                        }
                        visualStreamController.cancel();
                        clearActiveRequest();
                        onFinal?.(assistantMessage, finalPayload);
                        setRuntimeIsRunning(false);
                        transientAssistantMessageRef.current = null;
                        resolve(assistantMessage);
                    };

                    visualStreamController.waitForIdle().then(() => {
                        if (accumulatedStreamText.trim()) {
                            finalizationTimeoutId = setTimeout(() => {
                                finalizationTimeoutId = null;
                                completeRequest();
                            }, StreamFinalizationDelayMs);
                            return;
                        }

                        completeRequest();
                    });
                };

                const refreshCompletionTimeout = () => {
                    const activeRequest = activeRequestRef.current;
                    if (!activeRequest) return;

                    if (activeRequest.stallTimeoutId) {
                        clearTimeout(activeRequest.stallTimeoutId);
                    }

                    activeRequest.stallTimeoutId = setTimeout(() => {
                        finalizeFailedRequest(
                            isConnectedRef.current ? ChatErrorKind.GENERATION : ChatErrorKind.DISCONNECTED
                        );
                    }, ChatCompletionTimeoutMs);
                };

                const unsubscribe = onMessage((message) => {
                    let payload = getAiChatPayload(message);
                    const eventConversationId = getEventConversationId({ message, payload });
                    if (
                        !isEventForActiveRequest({
                            activeConversationId: activeRequestRef.current?.conversationId,
                            activeRequestId: activeRequestRef.current?.requestId,
                            chatId,
                            message,
                            payload,
                        })
                    ) {
                        return;
                    }
                    if (eventConversationId) {
                        if (activeRequestRef.current && !activeRequestRef.current.conversationId) {
                            activeRequestRef.current.conversationId = eventConversationId;
                            onChatRequestStateChange?.({
                                type: ChatRequestLifecycleEvent.CHAT_BOUND,
                                chatId: eventConversationId,
                            });
                        }
                        if (!payload.chat_id) {
                            payload = {
                                ...payload,
                                chat_id: eventConversationId,
                            };
                        }
                    }

                    if (message.type === CHAT_STARTED_TYPE) {
                        if (activeRequestRef.current?.startTimeoutId) {
                            clearTimeout(activeRequestRef.current.startTimeoutId);
                            activeRequestRef.current.startTimeoutId = null;
                        }

                        refreshCompletionTimeout();
                        return;
                    }

                    if (message.type === CHAT_ACTION_TYPE) {
                        const requestId = activeRequestRef.current?.requestId;
                        const requestedInteraction = getRequestedInteractionFromAction({ payload, requestId });
                        const messageId = payload.message_id || requestId;
                        pendingActionPayload = {
                            ...payload,
                            chat_id: payload.chat_id || chatId,
                            message_id: messageId,
                            message_text:
                                payload.message_text || accumulatedStreamText || requestedInteraction.prompt_text,
                            requested_interaction: requestedInteraction,
                        };

                        refreshCompletionTimeout();

                        if (payload.done) {
                            finalizeSuccessfulRequest(pendingActionPayload);
                        }
                        return;
                    }

                    if (message.type === CHAT_RETRIED_TYPE) {
                        refreshCompletionTimeout();
                        return;
                    }

                    if (message.type === CHAT_FAILED_TYPE) {
                        finalizeFailedRequest(
                            isUnsupportedRequestError(payload.error)
                                ? ChatErrorKind.UNSUPPORTED_REQUEST
                                : ChatErrorKind.GENERATION
                        );
                        return;
                    }

                    const streamTextUpdate = getStreamTextUpdateFromRuntimeEvent({
                        message,
                        payload,
                        streamedText: accumulatedStreamText,
                    });
                    if (streamTextUpdate) {
                        if (payload.message_id) {
                            transientAssistantMessageRef.current = {
                                ...(transientAssistantMessageRef.current || {}),
                                id: payload.message_id,
                            };
                        }
                        accumulatedStreamText =
                            streamTextUpdate.mode === ChatStreamUpdateMode.REPLACE
                                ? streamTextUpdate.text
                                : `${accumulatedStreamText}${streamTextUpdate.text}`;
                        refreshCompletionTimeout();
                        visualStreamController.enqueue(streamTextUpdate);

                        if (!payload.done) return;
                    }

                    if (
                        message.type === ChatEventType.LEGACY_FINAL ||
                        message.type === CHAT_RESPONSE_COMPLETED_TYPE ||
                        message.type === CHAT_COMPLETED_TYPE ||
                        (message.type === CHAT_RECEIVE_MESSAGE_TYPE && payload.done)
                    ) {
                        finalizeSuccessfulRequest(
                            getFinalPayloadFromRuntimeEvent({
                                fallbackMessageContext: latestMessageContext,
                                message,
                                payload,
                                streamedText: accumulatedStreamText,
                            })
                        );
                    }
                });

                activeRequestRef.current = {
                    cancelPendingResult: () => {
                        didCancelPendingResult = true;
                        didSettleRequest = true;
                        clearFinalizationTimeout();
                        visualStreamController.cancel();
                    },
                    fail: (failureReason) => finalizeFailedRequest(failureReason),
                    conversationId: chatId || null,
                    requestId,
                    startTimeoutId: null,
                    stallTimeoutId: null,
                    unsubscribe,
                    userMessageCreatedOn: userMessage.created_on || Date.now(),
                    userMessageId: userMessage.id,
                };

                const didSendRequest = sendMessage(
                    buildOutgoingRequest({
                        assignmentId,
                        chatId,
                        fileKey,
                        requestId,
                        userMessage,
                    })
                );

                if (!didSendRequest) {
                    finalizeFailedRequest(ChatErrorKind.DISCONNECTED);
                    return;
                }

                retryRequestRef.current = buildRetryRequestSnapshot(userMessage);
                clearChatError();

                if (shouldCreateUserMessage) {
                    onChatRequestStateChange?.({
                        type: ChatRequestLifecycleEvent.USER_MESSAGE_CREATED,
                        userMessage,
                    });
                }

                activeRequestRef.current.startTimeoutId = setTimeout(() => {
                    finalizeFailedRequest(
                        isConnectedRef.current ? ChatErrorKind.GENERATION : ChatErrorKind.DISCONNECTED
                    );
                }, ChatStartTimeoutMs);
            });
        },
        [
            assignmentId,
            buildOutgoingRequest,
            chatId,
            clearActiveRequest,
            clearChatError,
            fileKey,
            latestMessageContext,
            onMessage,
            onChatRequestStateChange,
            sendMessage,
            setRuntimeReconciliationBlocked,
        ]
    );

    useEffect(() => {
        if (isConnected || !activeRequestRef.current?.fail) return;
        activeRequestRef.current.fail(ChatErrorKind.DISCONNECTED);
    }, [isConnected]);

    useEffect(() => {
        if (isRuntimeReconciliationBlockedRef.current) return;
        if (chatIsProcessing) return;
        if (!hasPersistedAssistantResponseForRequest({ chat, request: activeRequestRef.current })) return;

        clearActiveRequest();
        resetTransientRuntimeState();
        transientAssistantMessageRef.current = null;
    }, [chat, chatIsProcessing, clearActiveRequest, resetTransientRuntimeState]);

    useEffect(() => {
        return () => {
            clearActiveRequest({ cancelPendingResult: true });
        };
    }, [clearActiveRequest]);

    useEffect(() => {
        const cleanup = onMessage((message) => {
            if (!getOutOfSyncChatReconciliation({ chatId, message })) return;
            if (activeRequestRef.current || isRuntimeReconciliationBlockedRef.current) return;

            resetRuntimeForChatReconciliation();
        });

        return cleanup;
    }, [chatId, onMessage, resetRuntimeForChatReconciliation]);

    useEffect(() => {
        if (chatId && activeRequestRef.current?.conversationId === chatId) return;

        clearActiveRequest({ cancelPendingResult: true });
        setChatError(null);
        setManualAssistantDraft(EmptyAssistantDraft);
        setIsRetrying(false);
        setRuntimeIsRunning(false);
        setRuntimeReconciliationBlocked(false);
        transientAssistantMessageRef.current = null;
        retryRequestRef.current = null;
    }, [chatId, clearActiveRequest, setChatError, setRuntimeReconciliationBlocked, threadRuntimeKey]);

    const onNewMessage = useCallback(
        async (message) => {
            const messageText = message.content
                .filter((part) => part.type === 'text')
                .map((part) => part.text || '')
                .join('\n\n')
                .trim();
            if (!messageText) return;

            clearChatError();
            setManualAssistantDraft(EmptyAssistantDraft);

            const timestamp = message.createdAt instanceof Date ? message.createdAt.getTime() : Date.now();
            const interactionResponse = message.metadata?.custom?.interaction_response;

            await executeRequest({
                onStreamUpdate: ({ mode, text }) => {
                    setManualAssistantDraft((currentDraft) =>
                        mode === ChatStreamUpdateMode.REPLACE ? text : `${currentDraft}${text}`
                    );
                },
                onFailure: (failureReason) => {
                    applyRequestFailure(failureReason);
                },
                onFinal: (assistantMessage, payload) => {
                    setManualAssistantDraft(EmptyAssistantDraft);
                    onChatRequestStateChange?.({
                        type: ChatRequestLifecycleEvent.ASSISTANT_MESSAGE_CREATED,
                        assistantMessage,
                        payload,
                    });
                },
                shouldCreateUserMessage: true,
                userMessage: {
                    id: uuid(),
                    active: true,
                    created_on: timestamp,
                    modified_on: timestamp,
                    role: ChatRole.USER,
                    message_text: messageText,
                    message_context: latestMessageContext,
                    ...(interactionResponse ? { interaction_response: interactionResponse } : {}),
                },
            });
        },
        [applyRequestFailure, clearChatError, executeRequest, latestMessageContext, onChatRequestStateChange]
    );

    const retryLastRequest = useCallback(() => {
        const retryRequest = retryRequestRef.current;
        if (!retryRequest) return;

        clearChatError();
        setIsRetrying(true);
        setManualAssistantDraft(EmptyAssistantDraft);

        executeRequest({
            onStreamUpdate: ({ mode, text }) => {
                setManualAssistantDraft((currentDraft) =>
                    mode === ChatStreamUpdateMode.REPLACE ? text : `${currentDraft}${text}`
                );
            },
            onFailure: (failureReason) => {
                applyRequestFailure(failureReason);
            },
            onFinal: (assistantMessage, payload) => {
                setIsRetrying(false);
                setManualAssistantDraft(EmptyAssistantDraft);
                onChatRequestStateChange?.({
                    type: ChatRequestLifecycleEvent.ASSISTANT_MESSAGE_CREATED,
                    assistantMessage,
                    payload,
                });
            },
            shouldCreateUserMessage: false,
            userMessage: buildRetryUserMessage({
                fallbackMessageContext: latestMessageContext,
                retryRequest,
            }),
        });
    }, [applyRequestFailure, clearChatError, executeRequest, latestMessageContext, onChatRequestStateChange]);

    const chatRuntimeContextValue = useMemo(() => {
        return {
            chatError,
            clearChatError,
            isRetrying,
            manualAssistantDraft,
            retryLastRequest,
        };
    }, [chatError, clearChatError, isRetrying, manualAssistantDraft, retryLastRequest]);

    const runtimeMessages = useMemo(() => {
        if (!manualAssistantDraft.trim() || !transientAssistantMessageRef.current) {
            return assistantMessages;
        }

        return toAssistantMessages([
            ...(chat?.messages || []),
            buildTransientAssistantMessage({
                createdOn: transientAssistantMessageRef.current.createdOn,
                id: transientAssistantMessageRef.current.id,
                text: manualAssistantDraft,
            }),
        ]);
    }, [assistantMessages, chat?.messages, manualAssistantDraft]);

    const runtime = useExternalStoreRuntime({
        adapters: {
            ...(threadListAdapter ? { threadList: threadListAdapter } : {}),
        },
        isRunning: runtimeIsRunning || isRetrying || (chatIsProcessing && !chatError),
        messages: runtimeMessages,
        onNew: onNewMessage,
    });

    return {
        chatRuntimeContextValue,
        runtime,
    };
};

export default useAssistantChatController;
